import { Sheet, SheetContent } from '@/shared/widgets/sheet';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { cn } from '@/shared/utils/cn';
import { X, ChevronDown } from 'lucide-react';
import {
  CustomizationYellowIcon,
  CustomizationRoseIcon,
  CustomizationWhiteIcon,
  CustomizationBrownIcon,
  Customization14ktIcon,
  Customization18ktIcon,
  Customization22ktIcon,
  CustomizationPlatinumIcon,
} from '@/shared/icons/icons';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';
import type { ParsedCustomizationOptions, GoldPurityOption } from '../../domain/models/CustomizationOptions';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PosCartItem } from '@/features/posCart/domain/models/PosCartItem';
import { getSizeSelectPlaceholder } from '../../utils/sizeSection.utils';
import { useCustomizationSheetV2 } from '../hooks/useCustomizationSheetV2';

interface CustomizationSheetV2Props {
  open: boolean;
  designName: string;
  draft: CustomizationSelection;
  parsedOptions: ParsedCustomizationOptions;
  goldPurities: GoldPurityOption[];
  previewPrice: number | null;
  isPreviewLoading: boolean;
  isPriceAnimating: boolean;
  isApplying: boolean;
  onClose: () => void;
  onOptionChange: (key: string, value: string | number) => void;
  onApply: () => void;
  onCancel: () => void;
  product: PosCartItem | null;
  productDetail: ProductDetail | null;
}

export default function CustomizationSheetV2({
  open,
  designName: _designName,
  draft,
  parsedOptions,
  goldPurities,
  previewPrice,
  isPreviewLoading,
  isPriceAnimating,
  isApplying,
  onClose,
  onOptionChange,
  onApply,
  onCancel,
  product,
  productDetail,
}: CustomizationSheetV2Props) {
  const {
    isMobile,
    isSizeDropdownOpen,
    setIsSizeDropdownOpen,
    isAnimatedOpen,
    hasChanged,
    handleClose,
    localPriceAnimating,
    handleOptionChangeWithAnimation,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    transformStyle,
  } = useCustomizationSheetV2({
    open,
    draft,
    onCancel,
    onOptionChange,
  });

  usePopupHistory({ isOpen: open, onClose, stateKey: 'customizationSheetV2' });

  const getGoldColorIcon = (colorKey: string) => {
    const normalized = colorKey.toLowerCase();
    if (normalized === 'gold') {
      return <CustomizationYellowIcon className="relative top-1 h-7 w-7 shrink-0" />;
    }
    if (normalized.includes('rose')) {
      return <CustomizationRoseIcon className="relative top-1 h-7 w-7 shrink-0" />;
    }
    if (normalized.includes('white')) {
      return <CustomizationWhiteIcon className="relative top-1 h-7 w-7 shrink-0" />;
    }
    if (normalized.includes('brown')) {
      return <CustomizationBrownIcon className="relative top-1 h-7 w-7 shrink-0" />;
    }
    return null;
  };

  const getPurityBadge = (purity: string) => {
    const normalized = purity.toLowerCase();
    if (normalized.includes('14')) {
      return <Customization14ktIcon className="h-5 w-5 shrink-0" />;
    }
    if (normalized.includes('18')) {
      return <Customization18ktIcon className="h-5 w-5 shrink-0" />;
    }
    if (normalized.includes('22')) {
      return <Customization22ktIcon className="h-5 w-5 shrink-0" />;
    }
    if (normalized.includes('950') || normalized.includes('plat')) {
      return <CustomizationPlatinumIcon className="h-5 w-5 shrink-0" />;
    }
    return null;
  };

  const availableSizes = parsedOptions.sizes || [];
  const showSizeSelector = availableSizes.length > 0;
  const sizePlaceholder = productDetail ? getSizeSelectPlaceholder(productDetail.category) : 'Size';

  if (!product) return null;

  const displayedPrice = hasChanged && previewPrice !== null
    ? formatDisplayPrice(previewPrice)
    : (product.discountedPrice ?? product.price);

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        showCloseButton={false}
        onPointerDownOutside={handleClose}
        style={transformStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          "flex flex-col gap-0 p-0 bg-white shadow-2xl overflow-hidden transition-transform duration-300 ease-out",
          isMobile
            ? cn("h-auto rounded-t-[32px]", isAnimatedOpen ? "translate-y-0!" : "translate-y-full!")
            : cn("h-full w-[440px] sm:max-w-md border-l border-gray-200 rounded-l-[16px]", isAnimatedOpen ? "translate-x-0!" : "translate-x-full!")
        )}
      >
        {isMobile && (
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>
        )}
        {/* Header Section */}
        <div className="relative flex items-start gap-4 p-5 pr-10 pt-10">
          <div className="h-18 w-18 shrink-0 flex items-center justify-center rounded-lg">
            <img
               src={product.imageUrl}
               alt={product.designName}
               className="h-20 w-20 object-contain rounded-lg"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-bold text-[#011E39] leading-snug truncate">
              {product.designName}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span
                className={cn(
                  "text-[16px] font-bold text-[#0E2F5E]",
                  isPreviewLoading && !(isPriceAnimating || localPriceAnimating) && "opacity-60",
                  (isPriceAnimating || localPriceAnimating) && "animate-customization-price-bounce"
                )}
              >
                {displayedPrice}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] font-semibold text-[#E26A5E]">
              {product.discountName}
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-1.5 hover:bg-gray-100 transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Customization Options Area */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* 1. Size Selection */}
          {showSizeSelector && (
            <div>
              <div className="text-[16px] font-bold text-[#0E2F5E] mb-3">Select {sizePlaceholder}</div>
              {isMobile ? (
                <div className="flex flex-row overflow-x-auto whitespace-nowrap scrollbar-none pb-1 gap-2.5 snap-x touch-pan-x" style={{ scrollbarWidth: 'none' }}>
                  {availableSizes.map((sz) => {
                    const isSelected = draft.size === sz.key;
                    return (
                      <button
                        key={sz.key}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('size', sz.key)}
                        className={cn(
                          "flex h-10 min-w-10 shrink-0 items-center justify-center rounded-lg border text-[16px] font-semibold px-3 transition-all duration-200 shadow-xs snap-start",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        {sz.value}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsSizeDropdownOpen(!isSizeDropdownOpen)}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-[16px] font-medium text-gray-700 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] outline-none shadow-xs"
                  >
                    <span>{draft.size ? availableSizes.find(s => s.key === draft.size)?.value || draft.size : `Select ${sizePlaceholder.toLowerCase()}`}</span>
                    <ChevronDown className="h-4.5 w-4.5 text-gray-400" />
                  </button>

                  {isSizeDropdownOpen && (
                    <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg animate-in fade-in slide-in-from-top-2 duration-150">
                      {availableSizes.map((sz) => {
                        const isSelected = draft.size === sz.key;
                        return (
                          <button
                            key={sz.key}
                            type="button"
                            onClick={() => {
                              handleOptionChangeWithAnimation('size', sz.key);
                              setIsSizeDropdownOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center px-4 py-3 text-left text-[14px] font-medium hover:bg-gray-50 transition-colors border-b",
                              isSelected ? "bg-[#EDF5FF] text-[#0E2F5E] font-bold" : "text-gray-700"
                            )}
                          >
                            {sz.value}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 2. Gold Color Selection */}
          {parsedOptions.goldColors.length > 0 && (
            <div>
              <div className="text-[16px] font-bold text-[#0E2F5E] mb-3">Gold Colour</div>
              {isMobile ? (
                <div className="flex flex-row overflow-x-auto whitespace-nowrap scrollbar-none pb-1 gap-2.5 snap-x touch-pan-x" style={{ scrollbarWidth: 'none' }}>
                  {parsedOptions.goldColors.map((color) => {
                    const isSelected = draft.metal_color === color.color;
                    return (
                      <button
                        key={color.color}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('metal_color', color.color)}
                        className={cn(
                          "relative flex items-center gap-1 rounded-lg border px-4 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs shrink-0 snap-start",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        {getGoldColorIcon(color.color)}
                        <span>{color.displayName}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2.5">
                  {parsedOptions.goldColors.map((color) => {
                    const isSelected = draft.metal_color === color.color;
                    return (
                      <button
                        key={color.color}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('metal_color', color.color)}
                        className={cn(
                          "relative flex items-center gap-1 rounded-lg border px-4 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        {getGoldColorIcon(color.color)}
                        <span>{color.displayName}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 3. Gold Purity Selection */}
          {goldPurities.length > 0 && (
            <div>
              <div className="text-[14px] font-bold text-[#0E2F5E] mb-3">Gold Purity</div>
              {isMobile ? (
                <div className="flex flex-row overflow-x-auto whitespace-nowrap scrollbar-none pb-1 gap-2.5 snap-x touch-pan-x" style={{ scrollbarWidth: 'none' }}>
                  {goldPurities.map((purity) => {
                    const isSelected = draft.metal_karatage === purity.metalPurity;
                    return (
                      <button
                        key={purity.metalPurity}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('metal_karatage', purity.metalPurity)}
                        className={cn(
                          "flex items-center gap-1 rounded-lg border px-4 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs shrink-0 snap-start",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        {getPurityBadge(purity.metalPurity)}
                        <span>{purity.metalPurity}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2.5">
                  {goldPurities.map((purity) => {
                    const isSelected = draft.metal_karatage === purity.metalPurity;
                    return (
                      <button
                        key={purity.metalPurity}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('metal_karatage', purity.metalPurity)}
                        className={cn(
                          "flex items-center gap-1 rounded-lg border px-4 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        {getPurityBadge(purity.metalPurity)}
                        <span>{purity.metalPurity}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 4. Diamond Quality Selection */}
          {!parsedOptions.isSolitaire && parsedOptions.diamonds.length > 0 && (
            <div>
              <div className="text-[16px] font-bold text-[#0E2F5E] mb-3">Diamond</div>
              {isMobile ? (
                <div className="flex flex-row overflow-x-auto whitespace-nowrap scrollbar-none pb-1 gap-2.5 snap-x touch-pan-x" style={{ scrollbarWidth: 'none' }}>
                  {parsedOptions.diamonds.map((diamond) => {
                    const isSelected = draft.diamond_type === diamond.value;
                    return (
                      <button
                        key={diamond.value}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('diamond_type', diamond.value)}
                        className={cn(
                          "flex items-center justify-center rounded-lg border px-5 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs shrink-0 snap-start",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        <span>{diamond.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2.5">
                  {parsedOptions.diamonds.map((diamond) => {
                    const isSelected = draft.diamond_type === diamond.value;
                    return (
                      <button
                        key={diamond.value}
                        type="button"
                        onClick={() => handleOptionChangeWithAnimation('diamond_type', diamond.value)}
                        className={cn(
                          "flex items-center justify-center rounded-lg border px-5 py-2.5 text-[16px] font-semibold transition-all duration-200 shadow-xs",
                          isSelected
                            ? "border-[#2563EB] bg-[#EDF5FF] text-[#0E2F5E] font-bold"
                            : "border-gray-200 bg-white text-[#4A5568] hover:border-gray-300"
                        )}
                      >
                        <span>{diamond.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Proceed Button */}
        <div className="shrink-0 border-t border-gray-100 p-5 bg-white">
          <button
            type="button"
            className="w-full rounded-lg bg-[#0E2F5E] py-3.5 text-center text-[14px] font-bold text-white shadow-md transition-all duration-200 hover:bg-[#082245] active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none"
            onClick={onApply}
            disabled={isApplying || isPreviewLoading || (showSizeSelector && !draft.size)}
          >
            {showSizeSelector && !draft.size ? `Select ${sizePlaceholder} to Proceed` : 'Proceed To Cart'}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
